import React from 'react';
import * as mui from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import CopyAllRoundedIcon from '@mui/icons-material/CopyAllRounded';
import NormalTextField from '../../../common/NormalTextField';
import ModernSelect from '../../../common/ModernSelect';

// Modern minimalist styled components
const StyledCard = styled(mui.Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  border: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.5),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const SectionTitle = styled(mui.Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const AccessProvisioningTab = ({
  selectedApps,
  provisioningData,
  selectedForm,
  selectedApprover,
  selectedGrantor,
  selectedProcessDiff,
  documentationOptions,
  approverOptions,
  grantorOptions,
  YesNoOptions,
  handleOpenCopyModal,
  handleFormChange,
  handleApprovalChange,
  handleGrantorChange,
  handleProcessDiffChange,
  handleChange,
  handleBlur,
}) => {
  return (
    <div>
      <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', textAlign: 'center' }}>
        <mui.Tooltip title="Copy Process" onClick={handleOpenCopyModal}>
          <mui.IconButton>
            <CopyAllRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>

        <mui.Tooltip title="Certify Accuracy">
          <mui.IconButton>
            <HistoryEduRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>

        <mui.Tooltip title="Download Process Narrative">
          <mui.IconButton>
            <FileDownloadRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>
      </mui.Box>

      <StyledCard>
        <SectionTitle variant="overline">Documentation</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          How is the user access request currently submitted and documented?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Documentation Form"
            isMultiSelect={true}
            placeholderText="Select Documentation Form"
            selectedOptions={selectedForm}
            selectOptions={documentationOptions}
            handleChange={handleFormChange}
          />
        </div>
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Approval</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Who are the required approvers of access requests in {selectedApps.APP_NAME}?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Approvers"
            isMultiSelect={true}
            placeholderText="Select Approvers"
            selectOptions={approverOptions}
            selectedOptions={selectedApprover}
            handleChange={handleApprovalChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If 'Other' is selected in the question above, provide the approver details here. Otherwise, you can leave this blank:
        </mui.Typography>
        <NormalTextField
          label=""
          name="APPROVERS_OTHER"
          value={provisioningData.APPROVERS_OTHER}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Access Granting</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Who is responsible in granting the access to the system?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Access Grantor"
            isMultiSelect={true}
            placeholderText="Select Access Grantor"
            selectOptions={grantorOptions}
            selectedOptions={selectedGrantor}
            handleChange={handleGrantorChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If 'Other' is selected in the question above, provide the approver details here. Otherwise, you can leave this blank:
        </mui.Typography>
        <NormalTextField
          label=""
          name="GRANTOR_OTHER"
          value={provisioningData.GRANTOR_OTHER}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Process Confirmation</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Are there any differences in the provisioning process between regular employees and temporary/contract based employees?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Confirmation"
            isMultiSelect={false}
            placeholderText="Select Confirmation"
            selectOptions={YesNoOptions}
            selectedOptions={selectedProcessDiff}
            handleChange={handleProcessDiffChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If your answer on the question above is 'Yes', briefly describe the differences in the process below:
        </mui.Typography>
        <NormalTextField
          isMultiLine={true}
          rows="5"
          name="PROCESS_DIFFERENCE_OTHER"
          value={provisioningData.PROCESS_DIFFERENCE_OTHER}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </StyledCard>
    </div>
  );
};

export default AccessProvisioningTab; 